// Density summaries so a row can be drawn at any zoom in one pass over the pixels.
//
// A stream's row is indexed by event, not by time: a pixel column [A, B) is answered
// from the bundle times themselves, exactly, at every zoom - see StreamRow. Nothing
// there scales with the run's span, so a 29-second trace draws a 3-microsecond window
// as sharply as a 3-millisecond one.
//
// The summary rows still bin. A chip's row is a per-bin max over its streams and the
// tiles lane a per-bin mean over sixteen tiles, and neither is a question about one
// sorted event list: they are drawn from the run's active span cut into bins of width
// w0 (a few cycles, or wider when the span is long enough that 2^18 bins would not
// fit), each holding how much of it the core spent issuing bundles, parked on a flag or
// another stall, or in empty bundles (compiler padding), plus which unit dominated.
// Each further level halves the bin count; a painter picks the level whose bin is at
// most a pixel wide and takes the max over the bins under each pixel.
//
// A summary row is binned exactly once, when something first asks for a level of it: a
// chip's streams are swept into a scratch grid at w0, and that grid is kept as level 0
// and halved up to the base level, the finest with at most BASE_BINS bins - which is as
// fine as a strip at 320 px or the timeline at a couple of thousand ever asks. Zooming
// in past the base level then costs one halving per level and no pass over the run,
// which is what level 0's bins are kept for: re-binning a chip took 50 to 400 ms, and
// every level above level 0 together is not as large as level 0 itself.
//
// The span is `model.span`, where the cores were busy: the host's bring-up before the
// first dispatch can be hundreds of milliseconds of virtual time with nothing to bin.

import { DOMINANT, DOMINANT_BIT } from "./isa.js";
import { lowerIndex, upperIndex } from "./dom.js";
import { rowKeyOf } from "./links.js";
import { IForest } from "./iforest.js";

const NONE = 255;
// The finest level kept without a zoom: more bins than a full-width view can draw.
const BASE_BINS = 4096;
// How many bundles decide a column's colour: the four cycles a level-0 bin was.
const DOM_BUNDLES = 4;

function dominantIndex(mask) {
  for (let i = 0; i < DOMINANT.length; i++) if (mask & DOMINANT_BIT[i]) return i;
  return NONE;
}

// How much of [a, b) a set of intervals covers, as the integral of how many of them are
// open: a piecewise-linear staircase sampled at every endpoint, so a query is one
// binary search per end and the answer is exact however short the interval is. The
// values are sums of durations, never of absolute times, so a nanosecond of coverage
// stays a nanosecond thirty seconds into the run.
//
// Intervals may overlap - two empty-bundle runs anchored to the same bundle do, and on
// a tile row half of them do - and then the overlap counts twice, which is what the
// grid's accumulate() did too; a column's fractions are normalised at the end.
class Ramp {
  // `starts` and `ends` are the endpoints of the same intervals, each sorted on its
  // own: the sweep only needs the two multisets.
  constructor(starts, ends) {
    const m = starts.length;
    const x = new Float64Array(2 * m), f = new Float64Array(2 * m), s = new Int32Array(2 * m);
    let i = 0, j = 0, k = -1, open = 0, acc = 0, prev = 0;
    while (i < m || j < m) {
      const t = i < m && (j >= m || starts[i] <= ends[j]) ? starts[i] : ends[j];
      if (k >= 0) {
        acc += open * (t - prev);
        if (x[k] !== t) { k++; x[k] = t; f[k] = acc; }
      } else { k = 0; x[0] = t; f[0] = 0; }
      while (i < m && starts[i] === t) { i++; open++; }
      while (j < m && ends[j] === t) { j++; open--; }
      s[k] = open;
      prev = t;
    }
    this.n = k + 1;
    this.x = x.slice(0, this.n); this.f = f.slice(0, this.n); this.s = s.slice(0, this.n);
  }

  static of(starts, ends) {
    const a = Float64Array.from(starts), b = Float64Array.from(ends);
    a.sort(); b.sort();
    return new Ramp(a, b);
  }

  at(t) {
    const n = this.n;
    if (!n || t <= this.x[0]) return 0;
    if (t >= this.x[n - 1]) return this.f[n - 1];
    const i = lowerIndex(this.x, t, n);
    return this.f[i] + this.s[i] * (t - this.x[i]);
  }

  span(a, b) { return this.at(b) - this.at(a); }

  get bytes() { return this.x.byteLength + this.f.byteLength + this.s.byteLength; }
}

// How much of [0, X) the bundles [t_i, t_i + cyc) cover, counting overlaps twice like
// the grid did. Every bundle below the straddling ones contributes a whole cycle, so
// only those issued within one cycle of X are looked at - two of them, in every trace
// seen, since a core issues one bundle per cycle and the rest is float rounding.
function issuedUpTo(t, n, cyc, X) {
  const above = upperIndex(t, X, n);              // issued before X
  const below = lowerIndex(t, X - cyc, n) + 1;    // and retired before X
  let s = cyc * below;
  for (let i = below; i < above; i++) s += X - t[i];
  return s;
}

// A stream row: its own events, indexed, rather than a grid over the run's span.
//
// The bundle times are sorted, so the bundles of a column are an index range - two
// binary searches - whose count is the range's width, whose busy fraction is the
// covered time at each end (issuedUpTo) and whose unit mask is an iforest OR over the
// range. The empty-bundle runs and the row's parks are intervals, which a Ramp covers;
// the stall a column should show is the largest of the parks it touches, an iforest max
// over the park range. Everything is exact and nothing is wider than the pixel asked
// for, so this is both the strip's summary and the deepest zoom's.
class StreamRow {
  constructor(cache, core) {
    this.cache = cache;
    this.core = core;
    this.extent = cache.extentOf(core);
    // A grid's shape, for the painters that still ask a row for one.
    this.w0 = cache.w0;
    this.levels = [];
  }

  // On the first paint: until then a row costs its extent, which the chip summary and
  // the row list need anyway.
  build() {
    if (this.bundleMask) return this;
    const core = this.core, m = this.cache.model, n = core.n, cyc = core.cyclePs;
    this.cyc = cyc;
    this.bundleMask = IForest.or(core.umask, n);
    // Empty-bundle runs, anchored to the bundle that follows them as fillCore anchors
    // them; they are not sorted by time (their steps are, their anchors need not be).
    const k = core.nopN.length;
    const na = new Float64Array(k), nb = new Float64Array(k);
    for (let i = 0; i < k; i++) {
      const prev = lowerIndex(core.step, core.nopStep[i], n), next = prev + 1, len = core.nopN[i];
      let start;
      if (next < n && !Number.isNaN(core.t[next])) start = core.t[next] - len * cyc;
      else if (prev >= 0) start = core.t[prev] + cyc;
      else start = this.cache.origin;
      na[i] = start; nb[i] = start + len * cyc;
    }
    na.sort(); nb.sort();
    this.nopR = new Ramp(na, nb);
    // Waits and other stalls on this stream, by start; a column shows the two classes
    // apart, and the worst stall among the parks it touches.
    const parks = (this.cache.parksByRow.get(core.key) || []).slice().sort((a, b) => a.t0 - b.t0);
    const p = parks.length;
    const t0 = new Float64Array(p), end = new Float64Array(p), stall = new Float32Array(p);
    const wa = [], we = [], ha = [], he = [];
    for (let i = 0; i < p; i++) {
      const w = parks[i], t1 = Math.max(w.t0, w.open ? m.tEnd : w.t1);
      t0[i] = w.t0; end[i] = t1; stall[i] = w.stallPs || 0;
      if (w.cls === "host") { ha.push(w.t0); he.push(t1); } else { wa.push(w.t0); we.push(t1); }
    }
    // Parks on one row do not overlap in any trace seen, so the ends are already sorted
    // and the range of parks a column touches is [first end at or after A, last start
    // before B). Running the max keeps that search monotone if ever they do overlap,
    // at the cost of a stall from a park that had already finished.
    for (let i = 1; i < p; i++) if (end[i] < end[i - 1]) end[i] = end[i - 1];
    this.parkT0 = t0; this.parkEnd = end;
    this.stallF = IForest.max(stall, p);
    this.waitR = Ramp.of(wa, we);
    this.hwaitR = Ramp.of(ha, he);
    return this;
  }

  // The bundles, empty runs and parks under [t0, t1), exactly.
  column(t0, t1, out) {
    out.busy = 0; out.wait = 0; out.hwait = 0; out.nop = 0; out.dom = NONE; out.mask = 0; out.count = 0; out.stallMax = 0; out.active = 0;
    const w = t1 - t0;
    if (!(w > 0)) return out;
    const core = this.core, t = core.t, n = core.n, cyc = this.cyc;
    const i0 = upperIndex(t, t0, n), i1 = upperIndex(t, t1, n);
    out.count = i1 - i0;
    if (i1 > i0) {
      out.mask = this.bundleMask.query(i0, i1);
      // The colour is the unit mix the column opens with, not its whole OR: a pixel
      // holding a thousand bundles touches every unit, and a row of that is one flat
      // colour. The grid coloured a pixel by its first level-0 bin for the same reason.
      out.dom = dominantIndex(i1 - i0 <= DOM_BUNDLES ? out.mask : this.bundleMask.query(i0, i0 + DOM_BUNDLES));
    }
    out.busy = (issuedUpTo(t, n, cyc, t1) - issuedUpTo(t, n, cyc, t0)) / w;
    out.nop = this.nopR.span(t0, t1) / w;
    out.wait = this.waitR.span(t0, t1) / w;
    out.hwait = this.hwaitR.span(t0, t1) / w;
    // The parks a column touches: those that had started before it ends and had not
    // finished when it began.
    const p = this.parkT0.length;
    const k0 = upperIndex(this.parkEnd, t0, p), k1 = upperIndex(this.parkT0, t1, p);
    if (k1 > k0) out.stallMax = this.stallF.query(k0, k1);
    const s = out.busy + out.wait + out.hwait + out.nop;
    if (s > 1) { out.busy /= s; out.wait /= s; out.hwait /= s; out.nop /= s; }
    return out;
  }

  get bytes() {
    if (!this.bundleMask) return 0;
    return this.bundleMask.bytes + this.stallF.bytes + this.nopR.bytes + this.waitR.bytes + this.hwaitR.bytes
      + this.parkT0.byteLength + this.parkEnd.byteLength;
  }
}

// A level's arrays cover bins [base, base + n) of the global grid: a tile that ran
// for a few microseconds of a five-second span holds a few hundred bins, not 2^18.
// A scratch grid is one of these with room for the whole span, whose window moves and
// is cleared for each stream that borrows it, so its arrays can be longer than `n`.
//
// A grid is filled in floats, and a kept one holds its idle fractions as sixteen-bit
// ones: a level 0 of up to 2^17 bins is kept for every summary row and every tile lane
// for as long as the run is open, and the fractions are most of what a bin costs.
// `scale` is what a reader multiplies them by -- 1 for a scratch grid, 1/65535 for a
// kept one. `busy` stays a float wherever it is: it is what a column's colour is chosen
// by, and rounding it would let two bins tie where one was plainly busier.
const FRAC = 65535;
class Level {
  constructor(w, n, origin, base = 0, Frac = Float32Array) {
    this.w = w; this.n = n; this.origin = origin; this.base = base;
    this.Frac = Frac;
    this.scale = Frac === Float32Array ? 1 : 1 / FRAC;
    this.busy = new Float32Array(n);
    this.wait = new Frac(n);               // waiting on another core (or a core's DMA)
    this.hwait = new Frac(n);              // waiting on the host
    this.nop = new Frac(n);
    this.count = new Uint32Array(n);
    this.mask = new Uint16Array(n);
    this.dom = new Uint8Array(n).fill(NONE);
    this.stallMax = new Float32Array(n);
  }
}

// Spread an interval [a, b) over the bins of `lvl`, adding its overlap fraction to `arr`.
function accumulate(lvl, arr, a, b) {
  if (!(b > a)) return;
  const w = lvl.w, o = lvl.origin + lvl.base * w;   // the level's own left edge
  let b0 = Math.floor((a - o) / w), b1 = Math.floor((b - o - 1e-6) / w);
  if (b1 < 0 || b0 >= lvl.n) return;
  if (b0 < 0) { a = o; b0 = 0; }
  if (b1 >= lvl.n) { b1 = lvl.n - 1; b = o + lvl.n * w; }
  if (b0 === b1) { arr[b0] += (b - a) / w; return; }
  arr[b0] += ((b0 + 1) * w + o - a) / w;
  for (let i = b0 + 1; i < b1; i++) arr[i] += 1;
  arr[b1] += (b - (b1 * w + o)) / w;
}

// One mip step. `dst` may be `src`: bin i reads only bins 2i and 2i + 1, and reads a
// pair before writing over it. A kept level's idle fractions are integers and are
// averaged as integers -- storing a half into a Uint16Array otherwise costs more than
// the pass.
function halve(src, dst) {
  const n = Math.ceil(src.n / 2);
  const odd = src.n & 1, whole = n - odd, int = src.Frac !== Float32Array;
  for (const f of ["busy", "wait", "hwait", "nop"]) {
    const s = src[f], d = dst[f];
    if (int && f !== "busy") for (let i = 0; i < whole; i++) d[i] = (s[2 * i] + s[2 * i + 1]) >> 1;
    else for (let i = 0; i < whole; i++) d[i] = (s[2 * i] + s[2 * i + 1]) / 2;
    if (odd) d[n - 1] = s[2 * (n - 1)];
  }
  for (let i = 0; i < n; i++) {
    const a = 2 * i, b = 2 * i + 1 < src.n ? 2 * i + 1 : a;
    const two = b !== a;
    const ca = src.count[a], cb = two ? src.count[b] : 0;
    dst.mask[i] = src.mask[a] | (two ? src.mask[b] : 0);
    dst.dom[i] = cb > ca ? src.dom[b] : src.dom[a];
    dst.stallMax[i] = Math.max(src.stallMax[a], two ? src.stallMax[b] : 0);
    dst.count[i] = ca + cb;
    if (src.active) dst.active[i] = Math.max(src.active[a], two ? src.active[b] : 0);
  }
  dst.w = src.w * 2; dst.n = n; dst.base = src.base >> 1; dst.origin = src.origin;
  return dst;
}

// The level above `p`, in arrays of its own -- and of its kind: halving a kept level
// averages its integers, which stay integers.
function halveNew(p) {
  const l = new Level(p.w * 2, Math.ceil(p.n / 2), p.origin, p.base >> 1, p.Frac);
  if (p.active) l.active = new Uint8Array(l.n);
  return halve(p, l);
}

// A grid's window in arrays sized to it, so the grid itself can be handed back. The
// idle fractions are quantised on the way in, downwards, so a bin still adds up to at
// most itself.
function snapshot(g) {
  const l = new Level(g.w, g.n, g.origin, g.base, Uint16Array);
  l.busy.set(g.busy.subarray(0, g.n));
  for (const f of ["wait", "hwait", "nop"]) {
    const src = g[f], dst = l[f];
    for (let i = 0; i < g.n; i++) dst[i] = Math.min(FRAC, src[i] * FRAC);
  }
  l.count.set(g.count.subarray(0, g.n));
  l.mask.set(g.mask.subarray(0, g.n));
  l.dom.set(g.dom.subarray(0, g.n));
  l.stallMax.set(g.stallMax.subarray(0, g.n));
  if (g.active) l.active = g.active.slice(0, g.n);
  return l;
}

function clear(g) {
  const n = g.n;
  g.busy.fill(0, 0, n); g.wait.fill(0, 0, n); g.hwait.fill(0, 0, n); g.nop.fill(0, 0, n);
  g.count.fill(0, 0, n); g.mask.fill(0, 0, n); g.dom.fill(NONE, 0, n); g.stallMax.fill(0, 0, n);
  g.active = null;                    // only a SparseCore's tiles keep a count there
}

// One stream's levels, each made the first time a painter asks for it.
class BinSet {
  constructor(cache, extent, fill, fillBase, extra) {
    this.w0 = cache.w0; this.origin = cache.origin;
    this.extent = extent;             // the [t0, t1] its bins must cover
    this.fill = fill;                 // fill(grid): this stream at w0
    this.fillBase = fillBase;         // make its base level, with its chip's if shared
    if (extra) Object.assign(this, extra);
    this.made = [];
    this.levels = [];
    for (let k = 0; k < cache.depth; k++)
      Object.defineProperty(this.levels, k, {
        enumerable: true, configurable: true,
        get: () => this.made[k] || cache.make(this, k),
      });
  }
}

export class BinsCache {
  constructor(model) {
    this.model = model;
    let maxCyc = 0;
    for (const c of model.cores) maxCyc = Math.max(maxCyc, c.cyclePs);
    if (!maxCyc) maxCyc = model.defaultCycle();
    const [a, b] = model.span;
    this.origin = a;
    this.w0 = Math.max(4 * maxCyc, Math.ceil((b - a) / (1 << 17)));
    this.n0 = Math.max(1, Math.ceil((b - a + 1) / this.w0));
    this.tc = new Map();
    this.chips = new Map();
    this.pool = [];                   // scratch grids, borrowed one stream at a time
    this.busy0 = null;                // the busiest chip per level-0 bin, for stretches()
    // Each stream's own waits and stalls, indexed once.
    this.parksByRow = new Map();
    const add = (k, x) => { let a = this.parksByRow.get(k); if (!a) { a = []; this.parksByRow.set(k, a); } a.push(x); };
    for (const w of model.waits) add(rowKeyOf(w.chip, w.core, w.ci, w.tile), w);
    for (const s of model.stalls) add(s.core.key, s);
  }

  levels() {
    const out = [];
    let w = this.w0, n = this.n0;
    for (;;) {
      out.push([w, n]);
      if (n <= 256) break;
      w *= 2; n = Math.ceil(n / 2);
    }
    return out;
  }

  binOf(l0, t) {
    return Math.min(l0.n - 1, Math.max(0, Math.floor((t - l0.origin) / l0.w) - l0.base));
  }

  // How many levels the full grid needs, so every stream's pyramid has the same
  // widths and any two can be combined level by level.
  get depth() {
    if (!this._depth) { let n = this.n0, d = 1; while (n > 256) { n = Math.ceil(n / 2); d++; } this._depth = d; }
    return this._depth;
  }

  // The finest level a strip or the timeline ever asks for.
  get baseK() {
    if (this._baseK == null) { let k = 0; while (k < this.depth - 1 && Math.ceil(this.n0 / 2 ** k) > BASE_BINS) k++; this._baseK = k; }
    return this._baseK;
  }

  // The level-0 window covering [t0, t1], its base rounded down to a multiple of
  // 2^depth so every coarser level's base is exact.
  windowFor(t0, t1) {
    const align = 1 << this.depth;
    let b0 = Math.floor((t0 - this.origin) / this.w0), b1 = Math.floor((t1 - this.origin) / this.w0) + 1;
    b0 = Math.max(0, Math.floor(b0 / align) * align);
    b1 = Math.min(this.n0, Math.ceil((b1 + 1) / align) * align);
    if (b1 <= b0) b1 = Math.min(this.n0, b0 + align);
    return [b0, Math.max(1, b1 - b0)];
  }

  // A cleared scratch grid over [t0, t1], at level 0.
  takeGrid(t0, t1) {
    const [base, n] = this.windowFor(t0, t1);
    const g = this.pool.pop() || new Level(this.w0, this.n0, this.origin);
    g.w = this.w0; g.n = n; g.base = base;
    clear(g);
    return g;
  }

  giveBack(g) { this.pool.push(g); }

  // The [t0, t1] a stream's bins must cover: its bundles, dispatches and waits.
  extentOf(core) {
    const m = this.model;
    let a = Infinity, b = -Infinity;
    if (core.n) { a = Math.min(a, core.t[0]); b = Math.max(b, core.t[core.n - 1] + core.cyclePs); }
    for (const d of core.dispatches) { a = Math.min(a, d.t0); b = Math.max(b, d.open ? m.tEnd : d.t1); }
    for (const w of this.parksByRow.get(core.key) || []) { a = Math.min(a, w.t0); b = Math.max(b, w.open ? m.tEnd : w.t1); }
    if (!Number.isFinite(a)) { a = this.origin; b = this.origin; }
    return [Math.max(a, this.origin), Math.min(b, this.origin + this.n0 * this.w0)];
  }

  core(core) {
    let b = this.tc.get(core.key);
    if (b) return b;
    b = new StreamRow(this, core);
    this.tc.set(core.key, b);
    return b;
  }

  // One SparseCore's sixteen tiles, together, straight from the tiles' bundles and
  // waits: per bin the mean busy fraction over the tiles that exist, the mean wait,
  // and how many tiles were active. No per-tile pyramid is built for this; a tile's
  // own bins are made only when its row is opened.
  tiles(chip, ci) {
    const key = `c${chip}s${ci}.tiles`;
    let b = this.tc.get(key);
    if (b) return b;
    const tiles = this.model.tilesOf(chip, ci);
    let e0 = Infinity, e1 = -Infinity;
    for (const t of tiles) { const [a, bb] = this.extentOf(t); e0 = Math.min(e0, a); e1 = Math.max(e1, bb); }
    if (!Number.isFinite(e0)) { e0 = this.origin; e1 = this.origin; }
    b = new BinSet(this, [e0, e1], (g) => this.fillTiles(tiles, g), () => this.chipBases(chip), { nTiles: tiles.length });
    this.tc.set(key, b);
    return b;
  }

  // A chip's summary: per bin, the busiest of its TensorCores and SparseCore
  // sequencers, plus the waits of SparseCores that left no stream.
  chip(chip) {
    let b = this.chips.get(chip);
    if (b) return b;
    b = new BinSet(this, [this.origin, this.origin + this.n0 * this.w0],
      (g) => this.fillChip(chip, g), () => this.chipBases(chip));
    this.chips.set(chip, b);
    return b;
  }

  // Fill in the level `bs` was asked for. Every level is a halving of the one below it
  // and level 0 is kept, so the row is binned only if it has never been binned at all -
  // the first paint at any zoom, not every zoom past the base level.
  make(bs, k) {
    const from = k < this.baseK ? 0 : this.baseK;
    if (!bs.made[from]) bs.fillBase();
    if (!bs.made[from]) this.alone(bs);             // a lane its chip's pass did not cover
    for (let j = from + 1; j <= k; j++) if (!bs.made[j]) bs.made[j] = halveNew(bs.made[j - 1]);
    return bs.made[k];
  }

  // A row binned on its own, for one no chip pass covers.
  alone(bs) {
    const g = this.takeGrid(bs.extent[0], bs.extent[1]);
    bs.fill(g);
    this.keepBase(bs, g);
    this.giveBack(g);
  }

  // A chip's summary, which is a per-bin max over its streams at w0: its tile lanes
  // fall out of the same pass, so they are kept as they go by.
  chipBases(chip) {
    const bs = this.chip(chip);
    const g = this.takeGrid(bs.extent[0], bs.extent[1]);
    this.fillChip(chip, g);
    this.keepBase(bs, g);
    this.giveBack(g);
  }

  // Level 0 and the base level of `bs`, from the grid it was just binned into (which
  // the halving consumes). The scratch grid is the only copy of the fine bins, so it is
  // taken here or not at all.
  keepBase(bs, g) {
    if (bs.made[this.baseK]) return;
    bs.made[0] = snapshot(g);
    for (let k = 0; k < this.baseK; k++) halve(g, g);
    if (this.baseK) bs.made[this.baseK] = snapshot(g);
  }

  fillCore(core, g) {
    const m = this.model, cyc = core.cyclePs;
    // Bundles: [t, t + cycle).
    for (let i = 0; i < core.n; i++) {
      const t = core.t[i];
      if (Number.isNaN(t)) continue;
      accumulate(g, g.busy, t, t + cyc);
      const bi = this.binOf(g, t);
      g.count[bi]++;
      g.mask[bi] |= core.umask[i];
    }
    // Empty-bundle runs: anchored to the bundle that follows them.
    for (let k = 0; k < core.nopN.length; k++) {
      const s = core.nopStep[k], n = core.nopN[k];
      const prev = lowerIndex(core.step, s, core.n);
      const next = prev + 1;
      let start;
      if (next < core.n && !Number.isNaN(core.t[next])) start = core.t[next] - n * cyc;
      else if (prev >= 0) start = core.t[prev] + cyc;
      else start = this.origin;
      accumulate(g, g.nop, start, start + n * cyc);
    }
    // Waits and other stalls on this stream.
    for (const w of this.parksByRow.get(core.key) || []) {
      const t1 = w.open ? m.tEnd : w.t1;
      accumulate(g, w.cls === "host" ? g.hwait : g.wait, w.t0, t1);
      const b0 = this.binOf(g, w.t0), b1 = this.binOf(g, t1);
      for (let i = b0; i <= b1; i++) if (w.stallPs > g.stallMax[i]) g.stallMax[i] = w.stallPs;
    }
    // The dominant unit of a bin is the first one its bundles make dominant, which is
    // the first one set in their mask; a bin never adds up to more than itself.
    for (let i = 0; i < g.n; i++) {
      if (g.mask[i]) g.dom[i] = dominantIndex(g.mask[i]);
      const s = g.busy[i] + g.wait[i] + g.hwait[i] + g.nop[i];
      if (s > 1) { g.busy[i] /= s; g.wait[i] /= s; g.hwait[i] /= s; g.nop[i] /= s; }
    }
  }

  fillTiles(tiles, g) {
    const m = this.model;
    const active = g.active = (this._active || (this._active = new Uint8Array(this.n0)));
    active.fill(0, 0, g.n);
    // One tile at a time, in a grid borrowed for its four fractions and a seen flag.
    const t = this.takeGrid(g.origin + g.base * g.w, g.origin + (g.base + g.n) * g.w);
    const busy = t.busy, wait = t.wait, hwait = t.hwait, nop = t.nop, seen = t.count;
    const nT = Math.max(1, tiles.length);
    for (const tile of tiles) {
      // Only the tile's own bins: one that ran for a few microseconds of the span
      // should not have the whole SparseCore's window cleared and scanned for it.
      const [ta, tb] = this.extentOf(tile);
      const lo = this.binOf(g, ta), hi = this.binOf(g, tb) + 1;
      busy.fill(0, lo, hi); wait.fill(0, lo, hi); hwait.fill(0, lo, hi); nop.fill(0, lo, hi); seen.fill(0, lo, hi);
      const cyc = tile.cyclePs;
      for (let i = 0; i < tile.n; i++) {
        const tt = tile.t[i];
        if (Number.isNaN(tt)) continue;
        accumulate(g, busy, tt, tt + cyc);
        const bi = this.binOf(g, tt);
        g.count[bi]++; g.mask[bi] |= tile.umask[i]; seen[bi] = 1;
      }
      for (const w of this.parksByRow.get(tile.key) || []) {
        const t1 = w.open ? m.tEnd : w.t1;
        accumulate(g, w.cls === "host" ? hwait : wait, w.t0, t1);
        const b0 = this.binOf(g, w.t0), b1 = this.binOf(g, t1);
        for (let i = b0; i <= b1; i++) { seen[i] = 1; if (w.stallPs > g.stallMax[i]) g.stallMax[i] = w.stallPs; }
      }
      for (let i = lo; i < hi; i++) {
        const s = busy[i] + wait[i] + hwait[i] + nop[i];
        const k = s > 1 ? 1 / s : 1;
        g.busy[i] += busy[i] * k / nT; g.wait[i] += wait[i] * k / nT; g.hwait[i] += hwait[i] * k / nT; g.nop[i] += nop[i] * k / nT;
        if (seen[i]) active[i]++;
      }
    }
    this.giveBack(t);
    for (let i = 0; i < g.n; i++) if (g.mask[i]) g.dom[i] = dominantIndex(g.mask[i]);
  }

  fillChip(chip, g) {
    const m = this.model;
    const cores = m.cores.filter((c) => c.chip === chip && c.kind !== "tile");
    for (const c of cores) {
      const s = this.takeGrid(...this.core(c).extent);
      this.fillCore(c, s);
      for (let j = 0; j < s.n; j++) {
        const i = j + s.base;
        if (s.busy[j] > g.busy[i]) { g.busy[i] = s.busy[j]; g.dom[i] = s.dom[j]; }
        if (s.wait[j] > g.wait[i]) g.wait[i] = s.wait[j];
        if (s.hwait[j] > g.hwait[i]) g.hwait[i] = s.hwait[j];
        if (s.nop[j] > g.nop[i]) g.nop[i] = s.nop[j];
        g.count[i] += s.count[j];
        g.mask[i] |= s.mask[j];
        if (s.stallMax[j] > g.stallMax[i]) g.stallMax[i] = s.stallMax[j];
      }
      this.giveBack(s);
    }
    // Tiles count too: the chip is busy when its tiles are.
    for (const c of cores) {
      if (c.kind !== "sc") continue;
      const bs = this.tiles(chip, c.ci);
      const s = this.takeGrid(bs.extent[0], bs.extent[1]);
      this.fillTiles(m.tilesOf(chip, c.ci), s);
      for (let j = 0; j < s.n; j++) { const i = j + s.base; if (s.busy[j] > g.busy[i]) { g.busy[i] = s.busy[j]; g.dom[i] = s.dom[j]; } }
      this.keepBase(bs, s);
      this.giveBack(s);
    }
    // A chip whose SparseCores left no stream at all has only their waits to show.
    if (!cores.length) {
      const scWait = new Float32Array(g.n);
      for (const w of m.waits) {
        if (w.core !== "SparseCore" || w.chip !== chip) continue;
        accumulate(g, scWait, w.t0, w.open ? m.tEnd : w.t1);
      }
      for (let i = 0; i < g.n; i++) if (scWait[i] > g.wait[i]) g.wait[i] = Math.min(1, scWait[i]);
    }
    normalise(g);
    // What stretches() reads, taken while the level-0 grid is here.
    const b0 = this.busy0 || (this.busy0 = new Float32Array(this.n0));
    for (let i = 0; i < g.n; i++) if (g.busy[i] > b0[i + g.base]) b0[i + g.base] = g.busy[i];
  }

  // Where any stream was issuing: [t0, t1] stretches from the chip rows, for
  // "next busy" navigation.
  stretches() {
    if (this._stretches) return this._stretches;
    const m = this.model;
    for (const chip of m.chipIds) void this.chip(chip).levels[this.baseK];   // fills busy0
    const l0 = this.busy0 || new Float32Array(this.n0);
    let out = [];
    let start = -1;
    const w = this.w0, o = this.origin;
    // Gaps under 0.5% of the span join two stretches; stretches under 0.2% are noise.
    const mergeBins = Math.max(8, Math.round(this.n0 * 0.005));
    const minBins = Math.max(2, Math.round(this.n0 * 0.002));
    for (let i = 0; i <= this.n0; i++) {
      const on = i < this.n0 && l0[i] > 0.02;
      if (on && start < 0) start = i;
      if (!on && start >= 0) {
        const last = out[out.length - 1];
        if (last && start - last.b1 < mergeBins) last.b1 = i;
        else out.push({ b0: start, b1: i });
        start = -1;
      }
    }
    const big = out.filter((s) => s.b1 - s.b0 >= minBins);
    out = (big.length ? big : out.slice(0, 1)).map((s) => ({ ...s, t0: o + s.b0 * w, t1: o + s.b1 * w }));
    this._stretches = out;
    return out;
  }
}

function normalise(l0) {
  for (let i = 0; i < l0.n; i++) {
    const s = l0.busy[i] + l0.wait[i] + l0.hwait[i] + l0.nop[i];
    if (s > 1) { l0.busy[i] /= s; l0.wait[i] /= s; l0.hwait[i] /= s; l0.nop[i] /= s; }
  }
}

// What to sample a row from at this zoom: a stream row has one answer at every zoom,
// a summary row the coarsest level whose bins are no wider than a pixel.
export function levelFor(binset, psPerPx) {
  if (binset.column) return binset.build();
  let k = 0;
  while (k + 1 < binset.levels.length && binset.w0 * 2 ** (k + 1) <= psPerPx) k++;
  return binset.levels[k];
}

// What is under [t0, t1), into `out` (reused across pixels): exactly, for a stream row,
// and the max over the bins it covers for a summary one.
export function sampleColumn(level, t0, t1, out) {
  if (level.column) return level.column(t0, t1, out);
  const w = level.w, o = level.origin + level.base * w;
  let b0 = Math.floor((t0 - o) / w), b1 = Math.floor((t1 - o - 1e-6) / w);
  if (b1 < b0) b1 = b0;
  out.busy = 0; out.wait = 0; out.hwait = 0; out.nop = 0; out.dom = NONE; out.mask = 0; out.count = 0; out.stallMax = 0; out.active = 0;
  if (b1 < 0 || b0 >= level.n) return out;
  if (b0 < 0) b0 = 0;
  if (b1 >= level.n) b1 = level.n - 1;
  for (let i = b0; i <= b1; i++) {
    if (level.busy[i] > out.busy) { out.busy = level.busy[i]; out.dom = level.dom[i]; }
    if (level.wait[i] > out.wait) out.wait = level.wait[i];
    if (level.hwait[i] > out.hwait) out.hwait = level.hwait[i];
    if (level.nop[i] > out.nop) out.nop = level.nop[i];
    if (level.active && level.active[i] > out.active) out.active = level.active[i];
    if (level.stallMax[i] > out.stallMax) out.stallMax = level.stallMax[i];
    out.mask |= level.mask[i];
    out.count += level.count[i];
  }
  const s = level.scale;
  if (s !== 1) { out.wait *= s; out.hwait *= s; out.nop *= s; }
  if (out.dom === NONE && out.mask) out.dom = dominantIndex(out.mask);
  return out;
}

export const DOM_NONE = NONE;
